import api from "@/utils/axios";
import { defineStore } from "pinia";

// Route/menu access rules, declared in route meta (see router/index.ts).
//   permissions: customer feature gates that must ALL be 1 ({stock:1, Parcels:1, ...}).
//                Applies to staff too — a staff member can never exceed the customer's features.
//   staffCodes:  staff action codes that must ALL be granted (["STOCK", ...]). Ignored for the account owner.
//   ownerOnly:   account owner only (API keys, webhooks, bank details, identity documents...).
// NOTE: this only hides UI. The API enforces the same rules (Back/routes/api.php, "access:" middleware).
export interface AccessRule {
  permissions?: string[];
  staffCodes?: string[];
  ownerOnly?: boolean;
}

// Deduplicates concurrent fetchPermissions() calls (startup, navigation, focus).
let inFlight: Promise<void> | null = null;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: null as string | null,
    user: null as Record<string, any> | null,
    // Always stored as { permKey: 1|0 }
    permissions: {} as Record<string, number>,
    // Staff-only: array of individual action codes ["FACTURES", "STOCK", ...]
    staffCodes: [] as string[],
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isStaff: (state) => !!state.user?.is_staff || !!state.user?.customer_id,
  },

  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setUser(user: Record<string, any>) {
      this.user = user;
    },
    clearAuth() {
      this.token = null;
      this.user = null;
      this.permissions = {};
      this.staffCodes = [];
    },

    // Revokes the token server-side, then clears local state. Local state is
    // cleared even if the request fails so the user is never stuck logged in.
    async logout() {
      if (this.token) {
        try {
          await api.post("/api/auth/client/user_logout");
        } catch {
          // token already invalid/expired — nothing to revoke
        }
      }
      this.clearAuth();
    },

    // Accepts either object {key: 1|0} or string[] from login responses
    setPermissions(permissions: Record<string, any> | string[]) {
      if (Array.isArray(permissions)) {
        const obj: Record<string, number> = {};
        permissions.forEach((k) => { obj[k] = 1; });
        this.permissions = obj;
      } else {
        this.permissions = permissions ?? {};
      }
    },

    fetchPermissions(): Promise<void> {
      if (!this.token || !this.user) return Promise.resolve();
      if (inFlight) return inFlight;

      inFlight = (async () => {
        try {
          const res = await api.get(`/api/auth/client/fecthPerms`);

          // Always refresh the stored user so is_staff stays current
          if (res.data.user) this.user = { ...this.user, ...res.data.user };

          // Computed after the merge above so a freshly-set placeholder user
          // (e.g. from a staff-link bootstrap) resolves correctly on the first call.
          if (this.isStaff) {
            // permissions = customer's feature gates {stock:1, Parcels:1, ...}
            this.permissions = res.data.user?.perms ?? {};
            // staffCodes = staff's individual action codes ["STOCK", "BON_LIVRAISON", ...]
            this.staffCodes = Array.isArray(res.data.data) ? res.data.data : [];
          } else {
            this.permissions = (res.data.data && typeof res.data.data === "object" && !Array.isArray(res.data.data))
              ? res.data.data
              : {};
          }
        } catch (error) {
          console.error("Failed to fetch permissions:", error);
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    },

    can(key: string) {
      return this.permissions[key] === 1;
    },

    // Single source of truth for both the router guard and the sidebar.
    canAccess(rule: AccessRule | undefined): boolean {
      if (!rule) return true;
      if (rule.ownerOnly && this.isStaff) return false;
      const perms = rule.permissions ?? [];
      if (!perms.every((p) => this.permissions[p] === 1)) return false;
      if (this.isStaff) {
        const codes = rule.staffCodes ?? [];
        if (!codes.every((c) => this.staffCodes.includes(c))) return false;
      }
      return true;
    },
  },

  persist: true,
});
